/**
 * **A TIFF's pages, as the engine draws them** (engine W16, T16.14, T16.19;
 * contract C-E12, plan D44) — and, from W30 (FR-67, D101), **an encrypted
 * PDF's whose password is saved**: the engine opens it with the sealed
 * password on the server, so the browser never holds the password or a
 * decrypted file — shared by the review screen's page
 * (`evidence-page.tsx`, with the evidence boxes) and the document's preview
 * (`preview.tsx`, without). Chrome, Edge and Firefox draw no TIFF, so the
 * browser never decodes one: the engine's sandbox draws each page as a PNG of
 * the page **as it lies** — after the file's own orientation tag, never turned
 * or straightened, at most 150 DPI and 4,000 px a side — and the service hands
 * it over as a logged read (purpose `view`).
 *
 * A page is fetched the first time a screen asks for it, decoded by the
 * screen's image opener, and kept for as long as that version is shown: paging
 * back reads nothing again. Every address made is released when the version
 * changes or the screen goes. A page past the file's last is `NOT_FOUND`, said
 * and not retried; a drawing that will not decode is said too; anything else is
 * a failure the person may try again.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { errorText } from './hooks'
import { ApiError, type ProDocClient } from './prodoc-client'

/** The one file kind the engine draws for the browser (D44) — every other image is the browser's own. */
export const TIFF = 'image/tiff'

/** The engine's drawing of a page is at most this resolution (D44) — a display size only, never an evidence box's. */
export const PREVIEW_DPI = 150

/** A decoded image, as a page needs it: an address the `<img>` shows, and its natural size in pixels. */
export interface ImageHandle {
  url: string
  width: number
  height: number
  destroy: () => void
}

/** Decode an image's bytes — a scan's own, or a TIFF page the engine drew as a PNG. Throws for one the browser cannot decode. */
export type ImageOpener = (blob: Blob) => Promise<ImageHandle>

/**
 * The default image opener: the browser's own decoder, from an object URL —
 * the bytes never leave the page. The browser applies the image's EXIF
 * orientation, as the engine did before reading it (`scanned/raster.py`).
 */
export const openImageInBrowser: ImageOpener = async (blob) => {
  if (typeof Image === 'undefined' || typeof URL.createObjectURL !== 'function') throw new Error('no image decoder')
  const url = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    if (!(image.naturalWidth > 0 && image.naturalHeight > 0)) throw new Error('no image')
    return { url, width: image.naturalWidth, height: image.naturalHeight, destroy: () => URL.revokeObjectURL(url) }
  } catch (e) {
    URL.revokeObjectURL(url)
    throw e
  }
}

/**
 * One page of a TIFF: on its way; drawn; not drawable — `missing` past the
 * file's last page, `unreadable` when the drawing will not decode — said, not
 * retried; or a failure to try again, in words.
 */
export type PageDrawing = { kind: 'loading' } | { kind: 'ready'; image: ImageHandle } | { kind: 'none'; reason: 'missing' | 'unreadable' | 'locked' } | { kind: 'error'; reason: string }

/**
 * W30 (FR-67, D101): the service would not draw an encrypted PDF's page —
 * `423`, `password_required` — because no password is saved for the version
 * (none ever was, or the screen's record is older than the answer).
 */
export const isPasswordRequired = (e: unknown): boolean => e instanceof ApiError && (e.status === 423 || e.reason === 'password_required' || e.code === 'PASSWORD_REQUIRED')

const NO_DRAWINGS: ReadonlyMap<number, PageDrawing> = new Map()

/** A drawing the opener would not decode. */
class Undecodable extends Error {}

/**
 * The pages of `versionId` as the engine draws them, while `enabled`:
 * `pages.get(n)` is page `n`'s state, `draw(n)` asks for it once, and
 * `draw(n, true)` asks again after a failure.
 */
export function usePageDrawings(client: ProDocClient, documentId: string, versionId: string, enabled: boolean, openImage: ImageOpener) {
  const key = `${documentId}\n${versionId}`
  const [drawn, setDrawn] = useState<{ key: string; pages: ReadonlyMap<number, PageDrawing> }>({ key, pages: NO_DRAWINGS })
  // This version's pages: what was asked for, and every address made, to release.
  const generation = useRef<{ key: string; asked: Set<number>; handles: ImageHandle[] } | null>(null)

  useEffect(() => {
    if (!enabled) return
    const own = { key, asked: new Set<number>(), handles: [] as ImageHandle[] }
    generation.current = own
    setDrawn({ key, pages: NO_DRAWINGS })
    return () => {
      if (generation.current === own) generation.current = null
      for (const handle of own.handles) handle.destroy()
    }
  }, [client, enabled, key])

  const draw = useCallback(
    (page: number, again = false) => {
      const own = generation.current
      if (!own || own.key !== key || (own.asked.has(page) && !again)) return
      own.asked.add(page)
      const set = (drawing: PageDrawing) => {
        if (generation.current !== own) return
        setDrawn((prev) => ({ key: own.key, pages: new Map(prev.key === own.key ? prev.pages : NO_DRAWINGS).set(page, drawing) }))
      }
      set({ kind: 'loading' })
      void client.documents.versions
        .pagePreview(documentId, versionId, page)
        .then(async (blob) => {
          try {
            return await openImage(blob)
          } catch {
            throw new Undecodable()
          }
        })
        .then((image) => {
          if (generation.current !== own) {
            image.destroy()
            return
          }
          own.handles.push(image)
          set({ kind: 'ready', image })
        })
        .catch((e) => {
          if (e instanceof Undecodable) set({ kind: 'none', reason: 'unreadable' })
          // W30: an encrypted PDF with no saved password — the screen asks for it instead.
          else if (isPasswordRequired(e)) set({ kind: 'none', reason: 'locked' })
          // Past the file's last page — said, not an error to retry.
          else if (e instanceof ApiError && e.code === 'NOT_FOUND') set({ kind: 'none', reason: 'missing' })
          else set({ kind: 'error', reason: errorText(e, 'The page could not be fetched.') })
        })
    },
    [client, documentId, versionId, key, openImage],
  )

  return { pages: drawn.key === key ? drawn.pages : NO_DRAWINGS, draw }
}
